import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { captureException } from 'shared/sentry';
import { fetchNotificationCount } from '../../actions/notificationAction';
import { AUTH_TOKEN_KEYS } from '../../constants/common';
import {
  SOCKET_NOTIFICATION_CONFIG,
  SOCKET_SENTRY_STEPS,
  SOCKET_SENTRY_TAGS,
} from './socket';
import {
  disconnectNotificationSocket,
  reconnectNotificationSocketIfNeeded,
  subscribeNotificationSocket,
} from './notificationSocket';
import { getLocalItem } from '../../utils/config/localStorage';
import { isLoginWithSupportPassword, isUserLoggedIn } from '../../utils/function';
import { mapSocketNotificationToForegroundDetail } from './socketNotificationNormalize';
import { dispatchForegroundPushEvent } from '../fcm/pushEvents';

const isAccessTokenSetEvent = (event) =>
  event?.detail?.key === AUTH_TOKEN_KEYS.ACCESS_TOKEN &&
  event?.detail?.action === 'set';

const isAccessTokenClearedEvent = (event) =>
  event?.detail?.key === AUTH_TOKEN_KEYS.ACCESS_TOKEN &&
  event?.detail?.action === 'remove';

const isAccessTokenStorageEvent = (event) =>
  event?.key === AUTH_TOKEN_KEYS.ACCESS_TOKEN && Boolean(event?.newValue);

const readAccessToken = () => {
  const token = getLocalItem(AUTH_TOKEN_KEYS.ACCESS_TOKEN, null);
  return typeof token === 'string' && token.length > 0 ? token : null;
};

const shouldConnectSocket = () => isUserLoggedIn() && !isLoginWithSupportPassword();

/**
 * App-level Socket.IO hook for `notification:new` and `socket:ready`.
 * Singleton socket with ref-counted subscribe; safe for Safari (polling fallback + visibility reconnect).
 */
export const useNotificationSocket = ({
  enabled = true,
  dispatchForegroundToasts = true,
} = {}) => {
  const dispatch = useDispatch();
  const countRefreshTimeoutRef = useRef(null);
  const unsubscribeRef = useRef(null);

  const scheduleNotificationCountRefresh = useCallback(() => {
    if (countRefreshTimeoutRef.current != null) {
      window.clearTimeout(countRefreshTimeoutRef.current);
    }

    countRefreshTimeoutRef.current = window.setTimeout(() => {
      countRefreshTimeoutRef.current = null;
      void dispatch(fetchNotificationCount());
    }, SOCKET_NOTIFICATION_CONFIG.COUNT_REFRESH_DEBOUNCE_MS);
  }, [dispatch]);

  const handleNotification = useCallback(
    (payload) => {
      if (dispatchForegroundToasts) {
        try {
          dispatchForegroundPushEvent(mapSocketNotificationToForegroundDetail(payload));
        } catch (error) {
          captureException(error, {
            tags: SOCKET_SENTRY_TAGS,
            extra: { step: SOCKET_SENTRY_STEPS.HANDLER, phase: 'foreground-toast' },
          });
        }
      }

      scheduleNotificationCountRefresh();
    },
    [dispatchForegroundToasts, scheduleNotificationCountRefresh]
  );

  const handleReady = useCallback(
    (ready) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('pushNotification >> socket:ready', ready);
      }
      scheduleNotificationCountRefresh();
    },
    [scheduleNotificationCountRefresh]
  );

  const connect = useCallback(() => {
    if (!shouldConnectSocket()) {
      disconnectNotificationSocket();
      return;
    }

    const token = readAccessToken();
    if (!token) {
      disconnectNotificationSocket();
      return;
    }

    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    unsubscribeRef.current = subscribeNotificationSocket(
      token,
      handleNotification,
      handleReady
    );
  }, [handleNotification, handleReady]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (!enabled) {
      disconnectNotificationSocket();
      return undefined;
    }

    connect();

    const onSameTabAuthChange = (event) => {
      if (isAccessTokenClearedEvent(event)) {
        disconnectNotificationSocket();
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        return;
      }
      if (isAccessTokenSetEvent(event)) {
        connect();
      }
    };

    const onStorage = (event) => {
      if (event?.key === AUTH_TOKEN_KEYS.ACCESS_TOKEN && !event.newValue) {
        disconnectNotificationSocket();
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        return;
      }
      if (isAccessTokenStorageEvent(event)) {
        connect();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (!shouldConnectSocket()) {
        return;
      }
      reconnectNotificationSocketIfNeeded();
    };

    window.addEventListener('localStorageChange', onSameTabAuthChange);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (countRefreshTimeoutRef.current != null) {
        window.clearTimeout(countRefreshTimeoutRef.current);
        countRefreshTimeoutRef.current = null;
      }
      window.removeEventListener('localStorageChange', onSameTabAuthChange);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibilityChange);

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [connect, enabled]);
};

export default useNotificationSocket;
